import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { gsap, Power1 } from 'gsap';
import Lottie from 'lottie-react';

import useClickEnter from '@/shared/hooks/useClickEnter';
import { useLetterPartyTheme } from '@/features/letterParty/theme';

import PolonaiseLine from '@/features/letterParty/components/startScreen/PolonaiseLine';
import AudioToggleButton from '@/shared/components/audioToggle/AudioToggleButton';
import BackButton from '@/shared/components/button/BackButton';
import Button from '@/shared/components/button/Button';
import Tooltip, { TooltipColors, TooltipSizes } from '@/shared/components/tooltip/Tooltip';

import '@/features/letterParty/styles/letter-party-start-screen.scss';

interface Props {
	onStart: () => void;
	onBack: () => void;
	bestScore?: number;
	showBestScore?: boolean;
}

const TOOLTIP_ANCHOR_ID = 'letter-party-start-screen-tooltip-anchor';
const REFERENCE_WIDTH = 1440;
const REFERENCE_HEIGHT = 800;

const StartScreen = ({ onStart, onBack, bestScore = 0, showBestScore = true }: Props) => {
	const { t } = useTranslation();
	const theme = useLetterPartyTheme();
	const rootRef = useRef<HTMLDivElement | null>(null);
	const hasStartedRef = useRef(false);
	const { palmTree, speaker } = theme.animations.startScreen;

	useLayoutEffect(() => {
		const root = rootRef.current;
		if (!root) {
			return;
		}

		const updateScale = () => {
			const { width, height } = root.getBoundingClientRect();
			const widthScale = width / REFERENCE_WIDTH;
			const heightScale = height / REFERENCE_HEIGHT;
			const scale = Math.min(widthScale, heightScale);

			root.style.setProperty('--letter-party-start-screen-scale', String(scale));
			root.style.setProperty('--letter-party-start-screen-background-scale-x', String(widthScale / scale));
			root.style.setProperty('--letter-party-start-screen-background-scale-y', String(heightScale / scale));
		};

		updateScale();
		if (typeof ResizeObserver === 'undefined') {
			window.addEventListener('resize', updateScale);
			return () => window.removeEventListener('resize', updateScale);
		}

		const resizeObserver = new ResizeObserver(updateScale);
		resizeObserver.observe(root);

		return () => resizeObserver.disconnect();
	}, []);

	const handleStart = () => {
		hasStartedRef.current = true;
		const el = rootRef.current;
		if (el) {
			gsap.to(el, {
				opacity: 0,
				duration: 0.3,
				ease: Power1.easeOut,
				onComplete: () => onStart(),
			});
		} else {
			onStart();
		}
	};

	useClickEnter(handleStart);

	return (
		<div
			className={`letter-party-game__start-screen letter-party-game__start-screen--${theme.key}`}
			role="dialog"
			aria-modal="true"
			aria-label={t('letter_party.start_screen.aria_label')}
			ref={rootRef}
		>
			<div className="letter-party-game__start-screen-stage">
				<div className="letter-party-game__start-screen-background">
					<div className="letter-party-game__start-screen-background-backward" />
					<PolonaiseLine />
					<Lottie
						animationData={palmTree}
						loop
						autoplay
						className="letter-party-game__start-screen-palm-tree-left"
					/>
					<Lottie animationData={speaker} loop autoplay className="letter-party-game__start-screen-speaker" />
					<Lottie
						animationData={palmTree}
						loop
						autoplay
						className="letter-party-game__start-screen-palm-tree-right"
					/>
					<div className="letter-party-game__start-screen-background-forward" />
				</div>

				<div className="letter-party-game__start-screen-controls">
					<div className="container">
						<BackButton onClick={onBack} />
						<AudioToggleButton tooltip={t('letter_party.audio_toggle.tooltip')} isOpen />
					</div>
				</div>

				<div
					id={TOOLTIP_ANCHOR_ID}
					className="letter-party-game__start-screen-tooltip-anchor"
					role="status"
					aria-live="polite"
				/>
				<Tooltip
					place="top"
					title={t('letter_party.start_screen.tooltip')}
					anchor={TOOLTIP_ANCHOR_ID}
					color={TooltipColors.LIGHT}
					size={TooltipSizes.LARGE}
					isOpen
					className="letter-party-game__start-screen-tooltip"
				/>
				<div className="btn-group center letter-party-game__start-screen-bottom">
					<Button
						type="primary"
						iconName="icon-play"
						title={t('letter_party.start_screen.button')}
						onClick={handleStart}
					/>
					{showBestScore && bestScore > 0 && (
						<div className="letter-party-game__start-screen-score">
							<span className="letter-party-game__start-screen-score-text">
								{t('letter_party.start_screen.longest_polonaise')}
							</span>
							<span className="letter-party-game__start-screen-score-number">{bestScore}</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default StartScreen;
